import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProjectFilters = ({ 
  filters, 
  activeFilters, 
  onFilterChange, 
  searchQuery, 
  onSearchChange,
  onClearFilters,
  projectCount 
}) => {
  const filterCategories = [
    {
      key: 'technology',
      label: 'Technology',
      icon: 'Code',
      options: filters.technologies
    },
    {
      key: 'type',
      label: 'Project Type',
      icon: 'Layers',
      options: filters.types
    },
    {
      key: 'industry',
      label: 'Industry',
      icon: 'Building',
      options: filters.industries
    },
    {
      key: 'status',
      label: 'Status',
      icon: 'Activity',
      options: filters.statuses
    }
  ];

  const hasActiveFilters = Object.values(activeFilters).some(arr => arr.length > 0) || searchQuery;

  return (
    <div className="bg-card border border-border rounded-xl p-6 mb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Filter" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Filter Projects</h3>
            <p className="text-sm text-text-secondary">
              {projectCount} project{projectCount !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>

        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            iconName="X"
            iconPosition="left"
            onClick={onClearFilters}
          >
            Clear All
          </Button>
        )}
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Icon 
            name="Search" 
            size={20} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" 
          />
          <input
            type="text"
            placeholder="Search projects by name, description, or technology..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-surface border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-premium"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-muted hover:text-text-primary transition-premium"
            >
              <Icon name="X" size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Filter Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {filterCategories.map((category) => (
          <div key={category.key} className="space-y-3">
            <div className="flex items-center space-x-2">
              <Icon name={category.icon} size={16} className="text-primary" />
              <h4 className="font-medium text-text-primary">{category.label}</h4>
            </div>
            
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {category.options.map((option) => {
                const isActive = activeFilters[category.key]?.includes(option.value);
                return (
                  <label
                    key={option.value}
                    className="flex items-center space-x-2 cursor-pointer group"
                  >
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={(e) => {
                        const newFilters = { ...activeFilters };
                        if (e.target.checked) {
                          newFilters[category.key] = [...(newFilters[category.key] || []), option.value];
                        } else {
                          newFilters[category.key] = (newFilters[category.key] || []).filter(v => v !== option.value);
                        }
                        onFilterChange(newFilters);
                      }}
                      className="w-4 h-4 text-primary bg-surface border-border rounded focus:ring-primary/20 focus:ring-2"
                    />
                    <span className={`text-sm transition-premium ${
                      isActive ? 'text-primary font-medium' : 'text-text-secondary group-hover:text-text-primary'
                    }`}>
                      {option.label}
                      <span className="text-text-muted ml-1">({option.count})</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="Filter" size={16} className="text-primary" />
            <span className="text-sm font-medium text-text-primary">Active Filters:</span>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {searchQuery && (
              <div className="flex items-center space-x-1 px-3 py-1 bg-primary/10 border border-primary/20 rounded-full">
                <Icon name="Search" size={12} className="text-primary" />
                <span className="text-xs font-medium text-primary">"{searchQuery}"</span>
                <button
                  onClick={() => onSearchChange('')}
                  className="text-primary hover:text-primary/70 transition-premium"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            )}
            
            {Object.entries(activeFilters).map(([category, values]) =>
              values.map((value) => {
                const categoryData = filterCategories.find(c => c.key === category);
                const option = categoryData?.options.find(o => o.value === value);
                return (
                  <div
                    key={`${category}-${value}`}
                    className="flex items-center space-x-1 px-3 py-1 bg-accent/10 border border-accent/20 rounded-full"
                  >
                    <Icon name={categoryData?.icon || 'Tag'} size={12} className="text-accent" />
                    <span className="text-xs font-medium text-accent">{option?.label || value}</span>
                    <button
                      onClick={() => {
                        const newFilters = { ...activeFilters };
                        newFilters[category] = newFilters[category].filter(v => v !== value);
                        onFilterChange(newFilters);
                      }}
                      className="text-accent hover:text-accent/70 transition-premium"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectFilters;